import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Alert,
  Tooltip,
  Button,
  Stack,
  Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table from '../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import {
  useENO1DispatchData,
  useAddENO1Sample,
  useUpdateENO1Sample,
  useENO1Finalize,
  useENO1Validate,
  SortConfig,
  FilterConfig,
} from './hooks';
import { useDeleteENO1Sample } from './hooks/useENO1SampleMutation';
import { useENO1FileDetails } from './hooks/useENO1FileDetails';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems, usePermission } from '../../../hooks';
import { MainContent } from './eNO1Message.styled';
import { ContentPaper } from './ViewSamples.styled';
import { ContextMenuAction } from '../../../common/components/Table/types';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { ValidationError } from '../../../hooks/useFileUpload';
import { ASIModal, ENO1EditModal } from './components';
import { isPlaygroundFile } from '../../../utils/filenameUtils';

// Custom hook for handling paginated errors
function usePaginatedErrors(errors: ValidationError[]) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' } | null>(null);

  // Ensure errors are properly formatted and assign IDs
  const processedErrors = useMemo(() => {
    if (!Array.isArray(errors)) {
      console.error('Errors is not an array:', errors);
      return [];
    }
    
    const processed = errors.map((error, index) => {
      // Ensure error has all required fields or provide defaults
      if (!error || typeof error !== 'object') {
        console.warn(`Invalid error at index ${index}:`, error);
        return {
          id: `error-${index}`,
          sampleId: null,
          version: null,
          field: 'unknown',
          errorCode: 'UnknownError',
          message: 'An unknown error occurred'
        };
      }
      
      return {
        id: `error-${index}`, // Add id for table row key
        sampleId: error.sampleId ?? null,
        version: error.version ?? null,
        field: error.field || 'unknown',
        errorCode: error.errorCode || 'Error',
        message: error.message || 'No error message provided'
      };
    });

    // Apply sorting if configured
    if (sortConfig) {
      return [...processed].sort((a, b) => {
        const aValue = a[sortConfig.field as keyof typeof a];
        const bValue = b[sortConfig.field as keyof typeof b];
        
        if (aValue === null) return sortConfig.direction === 'ASC' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'ASC' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ASC' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Default comparison for other types
        return sortConfig.direction === 'ASC' 
          ? (aValue < bValue ? -1 : 1) 
          : (aValue > bValue ? -1 : 1);
      });
    }
    
    return processed;
  }, [errors, sortConfig]);

  // Calculate total error count
  const totalErrorCount = processedErrors.length;

  // Get the paginated slice of errors for the current page
  const paginatedErrors = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return processedErrors.slice(startIndex, endIndex);
  }, [processedErrors, currentPage, pageSize]);

  // Sort errors based on field and direction
  const sortErrors = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    setSortConfig({ field, direction });
    setCurrentPage(0); // Reset to first page when sorting
  }, []);

  return {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  };
}

// Custom Error Table Component that handles its own pagination
const PaginatedErrorTable: React.FC<{ errors: ValidationError[], fileName?: string }> = ({ errors, fileName }) => {
  const {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  } = usePaginatedErrors(errors);

  // Define table columns with fixed widths to ensure alignment
  const columns = useMemo(() => [
    {
      Header: 'Sample ID',
      accessor: 'sampleId',
      width: '150px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string | null }) => (
        value !== null ? value : '-'
      )
    },
    {
      Header: 'Version',
      accessor: 'version',
      width: '100px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string | null }) => (
        value !== null ? value : '-'
      )
    },
    {
      Header: 'Field',
      accessor: 'field',
      width: '200px', // Fixed width to ensure alignment
    },
    {
      Header: 'Error Code',
      accessor: 'errorCode',
      width: '200px', // Fixed width to ensure alignment
    },
    {
      Header: 'Message',
      accessor: 'message',
      width: '500px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string }) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',  // Allow text to wrap to new lines
            wordBreak: 'break-word', // Break words to prevent overflow
            overflowWrap: 'break-word', // Ensure long words break and wrap
            lineHeight: 1.5,         // Increase line height for better readability
            padding: '8px 0',        // Add some padding for better spacing
          }}
        >
          {value}
        </Typography>
      )
    }
  ], []);

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    if (direction) {
      sortErrors(column, direction);
    }
  };

  // Early return if there are no valid errors
  if (totalErrorCount === 0) {
    return null;
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: '100%', 
        mt: 2,
        overflow: 'hidden', // Prevents horizontal overflow
        '& .MuiTableContainer-root': {
          overflow: 'auto'
        },
        '& .MuiTable-root': {
          tableLayout: 'fixed' // Forces the table to respect column widths
        }
      }}
      data-testid="paginated-error-table"
    >
      <Table
        data={paginatedErrors}
        columns={columns}
        moduleName={`Validation Errors for ${fileName || 'file'} (${totalErrorCount})`}
        pageSize={pageSize}
        page={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalCount={totalErrorCount}
        onSort={handleSort}
        viewMode={true} // Set to true to hide action buttons
        isRowClickable={false}
        disableAutoSizing={true} // Disable auto-sizing to use our fixed widths
      />
    </Paper>
  );
};

const ViewSamples: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/eno1', '');
  const { hasPermission: hasEditPermission } = usePermission('/eno1/samples/edit', '');

  // State to track the row being edited
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);

  // State for EditModal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // State to track if a new row is being added
  const [isAddingRow, setIsAddingRow] = useState<boolean>(false);

  // Store a temporary new row
  const [newRow, setNewRow] = useState<any>(null);

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // State for notification messages
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Add state for button loading
  const [validateCorrectLoading, setValidateCorrectLoading] = useState<boolean>(false);
  const [validateFinalizeLoading, setValidateFinalizeLoading] = useState<boolean>(false);

  // Add state for ASI Modal
  const [isASIModalOpen, setIsASIModalOpen] = useState<boolean>(false);
  const [selectedASIRow, setSelectedASIRow] = useState<any | null>(null);

  // Add search state management
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // Add status filter state management
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // State for multiple sorts
  const [sorts, setSorts] = useState<SortConfig[]>([]);
  
  // State for multiple filters
  const [filters, setFilters] = useState<FilterConfig[]>([]);

  // Mutation hooks for CRUD operations
  const addSampleMutation = useAddENO1Sample();
  const updateSampleMutation = useUpdateENO1Sample();
  const finalizeFile = useENO1Finalize();
  const validateFile = useENO1Validate();
  const deleteSampleMutation = useDeleteENO1Sample();

  // Function to extract validation errors from API responses
  const extractValidationErrors = (errorResponse: any): ValidationError[] => {
    if (!errorResponse) return [];
    
    // Check common error response patterns
    if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
      return errorResponse.errors;
    } else if (errorResponse.errorResponse && errorResponse.errorResponse.errors && 
               Array.isArray(errorResponse.errorResponse.errors)) {
      return errorResponse.errorResponse.errors;
    } else if (Array.isArray(errorResponse)) {
      return errorResponse;
    }
    
    return [];
  };

  // Get pagination parameters from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const sortField = searchParams.get('sort') || '';
  const sortDirection = (searchParams.get('direction') || '') as 'ASC' | 'DESC';

  // Check if edit mode is enabled via query parameter
  const isEditMode = searchParams.get('editMode') === 'true' && hasEditPermission;
  // Add explicit view mode flag
  const isViewMode = !isEditMode && hasPermission;

  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const isValidFileId = !isNaN(numericFileId) && numericFileId > 0;

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams): SortConfig[] => {
    const sortParams = params.getAll('sort');
    const parsedSorts: SortConfig[] = [];
    
    sortParams.forEach((sortParam) => {
      const [column, direction] = sortParam.split(',');
      if (column && (direction === 'ASC' || direction === 'DESC')) {
        parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    });
    
    return parsedSorts;
  }, []);

  // Helper function to update URL with multiple sorts
  const updateSortsInURL = useCallback((newSorts: SortConfig[], params: URLSearchParams) => {
    // Remove all existing sort parameters
    params.delete('sort');
    
    // Add new sort parameters
    newSorts.forEach((sort) => {
      params.append('sort', `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams): FilterConfig[] => {
    const filters: FilterConfig[] = [];
    // Get all filter parameters
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith('filter') && key.endsWith('Col')
    );
    
    filterKeys.forEach(key => {
      const colSuffix = key.replace('filter', '').replace('Col', '');
      const colValue = params.get(key);
      const termKey = `filter${colSuffix}Term`;
      const termValue = params.get(termKey);
      
      if (colValue && termValue) {
        filters.push({ column: colValue, term: termValue });
      }
    });
    
    // Also check for legacy single filter format
    const legacyCol = params.get('searchColumn');
    const legacyTerm = params.get('searchTerm');
    if (legacyCol && legacyTerm && !filters.some(f => f.column === legacyCol)) {
      filters.push({ column: legacyCol, term: legacyTerm });
    }
    
    return filters;
  }, []);

  // Helper function to update URL with multiple filters
  const updateFiltersInURL = useCallback((newFilters: FilterConfig[], params: URLSearchParams) => {
    // Remove all existing filter parameters
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith('filter') || key === 'searchColumn' || key === 'searchTerm'
    );
    filterKeys.forEach(key => params.delete(key));
    
    // Add new filter parameters
    newFilters.forEach((filter, index) => {
      params.set(`filter${index}Col`, filter.column);
      params.set(`filter${index}Term`, filter.term);
    });
  }, []);

  // Parse multiple sorts and filters from URL on mount and when URL changes
  useEffect(() => {
    const parsedSorts = parseSortsFromURL(searchParams);
    const parsedFilters = parseFiltersFromURL(searchParams);
    const statusParam = searchParams.get('status');
    
    // Only update if sorts actually changed to prevent infinite loops
    if (JSON.stringify(parsedSorts) !== JSON.stringify(sorts)) {
      setSorts(parsedSorts);
    }
    if (JSON.stringify(parsedFilters) !== JSON.stringify(filters)) {
      setFilters(parsedFilters);
    }
    setStatusFilter((prev) => {
      const newValue = statusParam || 'all';
      return prev !== newValue ? newValue : prev;
    });
  }, [searchParams, parseSortsFromURL, parseFiltersFromURL, sorts, filters]);

  const handleBack = () => {
    navigate('/eno1/messages');
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  // Reset validation errors
  const resetValidationErrors = () => {
    setValidationErrors([]);
  };

  // Update URL when pagination parameters change
  const updateSearchParams = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update the search params with the new values
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }

    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    updateSearchParams({ status });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // First clear the size parameter to force a query parameter change
    const tempParams = new URLSearchParams(searchParams);
    tempParams.delete('size');
    tempParams.delete('page');
    setSearchParams(tempParams);

    // Then set it again with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateSearchParams({
        page: '0', // Reset to first page when changing page size
        size: newSize.toString(),
      });
    }, 0);
  };

  // Handle sorting and searching with multi-sort and multi-filter support
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Handle multiple sorts
    let newSorts: SortConfig[] = [...sorts];
    
    if (direction && direction !== 'undefined' as any) {
      // Check if this column is already in the sorts array
      const existingIndex = newSorts.findIndex((s) => s.column === column);
      
      if (existingIndex >= 0) {
        // Update existing sort direction
        newSorts[existingIndex].direction = direction;
      } else {
        // Add new sort
        newSorts.push({ column, direction });
      }
    } else {
      // Remove sort for this column if direction is undefined
      newSorts = newSorts.filter((s) => s.column !== column);
    }
    
    // Update sorts state
    setSorts(newSorts);
    
    // Update URL with new sorts
    updateSortsInURL(newSorts, newSearchParams);
    
    // Handle multiple filters
    let newFilters: FilterConfig[] = [...filters];
    
    if (searchColumn !== undefined) {
      if (searchTerm && searchTerm.trim()) {
        // Check if this column already has a filter
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        
        if (existingFilterIndex >= 0) {
          // Update existing filter term
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          // Add new filter
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else {
        // Remove filter for this column if searchTerm is empty or undefined
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
    }
    
    // Update filters state
    setFilters(newFilters);
    
    // Update URL with new filters
    updateFiltersInURL(newFilters, newSearchParams);
    
    // Update legacy state for backward compatibility
    setSearchColumn(searchColumn);
    setSearchTerm(searchTerm);
    
    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }
    
    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Fetch dispatch data for the selected file with pagination parameters
  const {
    data: dispatchResponse,
    isLoading,
    isError,
    error,
    refetch: refetchDispatchData,
  } = useENO1DispatchData({
    eno1FileId: numericFileId,
    page,
    size: pageSize,
    sorts: sorts.length > 0 ? sorts : undefined,
    filters: filters.length > 0 ? filters : undefined,
    sort: sortField, // Legacy support
    direction: sortDirection, // Legacy support
    enabled: isValidFileId,
    searchColumn: searchColumn, // Legacy support
    searchTerm: searchTerm, // Legacy support
    status: statusFilter,
  });

  // Prepare data for the table
  const dispatchData = useMemo(() => {
    return dispatchResponse?.content || [];
  }, [dispatchResponse]);

  // Use the new hook to fetch file details
  const {
    data: fileDetails,
    isLoading: isFileDetailsLoading,
    isError: isFileDetailsError,
  } = useENO1FileDetails({
    fileId: numericFileId,
    enabled: isValidFileId,
  });

  // Check if file status allows validation actions
  const isValidateCorrectEnabled = useMemo(() => {
    // Enable if file status is DRAFT or D
    return fileDetails?.fileStatus === 'D' || fileDetails?.fileStatus === 'DRAFT';
  }, [fileDetails?.fileStatus]);

  const isValidateFinalizeEnabled = useMemo(() => {
    // Enable if file status does NOT have F or FINAL
    return fileDetails?.fileStatus !== 'F' && fileDetails?.fileStatus !== 'FINAL';
  }, [fileDetails?.fileStatus]);

  // Define columns for the table
  const columns = useMemo(
    () => [
      // Main columns in specified order
      {
        Header: 'sample_status',
        accessor: 'sampleStatus',
      },
      {
        Header: 'ASI',
        accessor: 'asi',
        width: '70px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
      {
        Header: 'sample_id',
        accessor: 'sampleId',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Explicitly convert to string for display
        },
      },
      {
        Header: 'version',
        accessor: 'version',
      },
      {
        Header: 'change_by',
        accessor: 'changeBy',
      },
      {
        Header: 'comment',
        accessor: 'comment',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          
          const commentText = value !== null && value !== undefined ? String(value) : '';
          
          if (!commentText) return '';
          
          // Truncate long comments (show first 50 characters)
          const maxLength = 50;
          const isLongComment = commentText.length > maxLength;
          const truncatedText = isLongComment 
            ? commentText.substring(0, maxLength) + '...' 
            : commentText;
          
          return (
            <Tooltip title={isLongComment ? commentText : ''} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                  cursor: isLongComment ? 'help' : 'default',
                }}
              >
                {truncatedText}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        Header: 'del_flag',
        accessor: 'delFlag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'valid_flag',
        accessor: 'validFlag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'mail_category',
        accessor: 'mailCategory',
      },
      {
        Header: 'format_corrected',
        accessor: 'formatCorrected',
      },
      {
        Header: 'sampling_method',
        accessor: 'samplingMethod',
      },
      {
        Header: 'net_or_gross_accounting',
        accessor: 'netOrGrossAccounting',
      },
      {
        Header: 'sampling_rate',
        accessor: 'samplingRate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'sending_ppo',
        accessor: 'sendingPpo',
      },
      {
        Header: 'oe_from_code',
        accessor: 'oeFromCode',
      },
      {
        Header: 'receiving_ppo',
        accessor: 'receivingPpo',
      },
      {
        Header: 'oe_to_code',
        accessor: 'oeToCode',
      },
      {
        Header: 'sampling_date',
        accessor: 'samplingDate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'receptacle_number',
        accessor: 'receptacleNumber',
      },
      {
        Header: 'despatch_number',
        accessor: 'despatchNumber',
      },
      {
        Header: 'despatch_date',
        accessor: 'despatchDate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'gross_kg',
        accessor: 'grossKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'tare_kg',
        accessor: 'tareKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'net_kg',
        accessor: 'netKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'p_kg',
        accessor: 'pkg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'g_kg',
        accessor: 'gkg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'e_kg',
        accessor: 'ekg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'p_items',
        accessor: 'pitems',
      },
      {
        Header: 'g_items',
        accessor: 'gitems',
      },
      {
        Header: 'e_items',
        accessor: 'eitems',
      },
      {
        Header: 'despatch_mail_subclass',
        accessor: 'despatchMailSubclass',
      },
      {
        Header: 'subsample_rate',
        accessor: 'subsampleRate',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'stratum',
        accessor: 'stratum',
      },
      {
        Header: 'received_weight_stratum',
        accessor: 'receivedWeightStratum',
      },
      {
        Header: 'sampled_weight_stratum',
        accessor: 'sampledWeightStratum',
      },
      {
        Header: 'effective_sampling_rate_stratum',
        accessor: 'effectiveSamplingRateStratum',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'barcode',
        accessor: 'barcode',
      },
      {
        Header: 'r+v',
        accessor: 'rplusV',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'f_bag',
        accessor: 'fbag',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value); // Convert any value to string for safe display
        },
      },
      {
        Header: 'receptacle_type',
        accessor: 'receptacleType',
      },
      {
        Header: 'receptacle_category',
        accessor: 'receptacleCategory',
      },
      {
        Header: 'receptacle_mail_subclass',
        accessor: 'receptacleMailSubclass',
      },
      {
        Header: 'label_weight',
        accessor: 'labelWeight',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '';
          return String(value);
        },
      },
      {
        Header: 'ibrs_kg',
        accessor: 'ibrsKg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'format_announced',
        accessor: 'formatAnnounced',
      },
      {
        Header: 'c_kg',
        accessor: 'ckg',
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined) return '-';
          return String(value);
        },
      },
      {
        Header: 'c_items',
        accessor: 'citems',
      },
      {
        Header: 'received_receptacles_stratum',
        accessor: 'receivedReceptaclesStratum',
      },
    ],
    []
  );

  // Define which columns are essential for mobile view
  const essentialColumns = [
    'sample_id',
    'barcode',
    'sample_status',
    'version',
    'mail_category',
    'gross_kg',
    'net_kg',
  ];

  // Filter columns based on screen size
  const filteredColumns = useMemo(() => {
    // On mobile, only show essential columns
    if (isMobile) {
      return columns.filter((column) => essentialColumns.includes(column.accessor as string));
    }
    // On desktop, show all columns
    return columns;
  }, [columns, isMobile, essentialColumns]);

  // Handle creating a new row
  const handleAddRow = () => {
    // Create a default new row with empty values or defaults
    const defaultNewRow = {
      id: 'new-row-' + Date.now(), // Temporary ID for UI purposes
      sampleId: '', // Ensure this is empty string, not null or number
      barcode: '',
      version: 0,
      changeBy: '', // This might be filled with current user
      comment: false,
      delFlag: 0,
      validFlag: 1,
      mailCategory: '',
      formatCorrected: '',
      formatAnnounced: '',
      samplingMethod: '',
      netOrGrossAccounting: '',
      samplingRate: '',
      samplingDate: null,
      subsampleRate: '',
      // Get sendingPpo and receivingPpo from fileDetails if available
      sendingPpo: fileDetails?.operatorByOutboundPpo || '',
      oeFromCode: '',
      receivingPpo: fileDetails?.operatorByInboundPpo || '',
      oeToCode: '',
      exchangeOfficeByInboundOe: '',
      exchangeOfficeByOutboundOe: '',
      receptacleNumber: '',
      despatchNumber: '',
      despatchDate: null,
      despatchMailSubclass: '',
      grossKg: '',
      tareKg: '',
      netKg: '',
      labelWeight: '',
      ibrsKg: '',
      pkg: '',
      gkg: '',
      ekg: '',
      ckg: '',
      pitems: null,
      gitems: null,
      eitems: null,
      citems: null,
      receptacleType: '',
      receptacleCategory: '',
      receptacleMailSubclass: '',
      rplusV: '',
      fbag: '',
      stratum: '',
      receivedWeightStratum: '',
      sampledWeightStratum: '',
      effectiveSamplingRateStratum: '',
      receivedReceptaclesStratum: '',
      // Include the file ID from the current context
      fileId: numericFileId,
      dataSetId: numericFileId, // Add dataSetId that matches fileId
      isNew: true, // Flag to identify this is a new row
    };

    // Set new row data and open the edit modal
    setNewRow(defaultNewRow);
    setSelectedRow(defaultNewRow);
    setIsAddingRow(true);
    setIsEditModalOpen(true);
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Define handlers for cell editing
  const handleCellEdit = (row: any, column: string, value: any) => {

    // If this is the new row we're editing, update our local state
    if (isAddingRow && row.id === newRow?.id) {
      // For numeric fields that might be represented as strings (e.g., grossKg, netKg, ekg),
      // we preserve the string format if it comes from the API that way
      setNewRow((prev: any) => ({
        ...prev,
        [column]: value,
      }));
    }
    // For existing rows, we'll handle this when saving
  };

  const handleRowSave = async (row: any, originalRow: any) => {

    // Prevent saving rows with valid_flag = 0
    if (row.validFlag === 0 && !isAddingRow) {
      setNotification({
        open: true,
        message: t('eno1.viewSamples.cannotEditInvalidRow', 'Only valid rows can be edited'),
        type: 'error',
      });
      return false;
    }

    // Handle new row save
    if (isAddingRow && row.id === newRow?.id) {
      try {
        // Prepare data for API submission - remove UI-only fields
        const { id, isNew, className, ...apiData } = row;

        // Ensure sampleId is a string
        const payload = {
          ...apiData,
          sampleId: apiData.sampleId !== null && apiData.sampleId !== undefined ? String(apiData.sampleId) : '',
          dataSetId: numericFileId, // Use dataSetId instead of fileId
        };

        // Remove fileId if it exists to ensure dataSetId is used
        if ('fileId' in payload) {
          delete payload.fileId;
        }

        // Call the mutation to add a new sample
        const response = await addSampleMutation.mutateAsync(payload);

        // Check if the response contains a different dataSetId than current fileId
        if (response?.dataSetId && response.dataSetId.toString() !== fileId) {
          // Navigate to the correct dataset page
          navigate(
            `/eno1/messages/samples/${response.dataSetId}${isEditMode ? '?editMode=true' : ''}`
          );
          return true;
        }

        // Clear the adding state and show success message
        setIsAddingRow(false);
        setNewRow(null);
        setNotification({
          open: true,
          message: t('eno1.viewSamples.addSuccess', 'New sample added successfully'),
          type: 'success',
        });

        return true; // Return true if save was successful
      } catch (error) {
        console.error('Error saving new row', error);
        setNotification({
          open: true,
          message: t('eno1.viewSamples.addError', 'Failed to add new sample'),
          type: 'error',
        });
        return false;
      }
    }

    // Handle updating an existing row
    try {
      // Prepare the data for the API by removing UI-specific fields
      // and mapping to the structure expected by the API
      const {
        id,
        className,
        isNew,
        // Remove any other UI-only fields here
        ...cleanData
      } = row;

      // Ensure sampleId is a string in the payload
      const payload = {
        ...cleanData,
        sampleId: cleanData.sampleId !== null && cleanData.sampleId !== undefined ? String(cleanData.sampleId) : '',
        // Always set these IDs explicitly
        dataSetId: numericFileId, // Use dataSetId instead of fileId
        eno1FileId: numericFileId,
        fileId: numericFileId
      };


      // Call the mutation to update the sample
      const response = await updateSampleMutation.mutateAsync(payload);

      // Check if the response contains a different dataSetId than current fileId
      if (response?.dataSetId && response.dataSetId.toString() !== fileId) {
        // Navigate to the correct dataset page
        navigate(
          `/eno1/messages/samples/${response.dataSetId}${isEditMode ? '?editMode=true' : ''}`
        );
        return true;
      }

      if (response?.dataSetId && response.dataSetId.toString() === fileId) {
        // Navigate to the correct dataset page
        window.location.reload();
        return true;
      }

      setNotification({
        open: true,
        message: t('eno1.viewSamples.updateSuccess', 'Sample updated successfully'),
        type: 'success',
      });

      setEditingRowId(null); // Clear editing state after save
      return true; // Return true if save was successful
    } catch (error) {
      console.error('Error updating row', error);
      setNotification({
        open: true,
        message: t('eno1.viewSamples.updateError', 'Failed to update sample'),
        type: 'error',
      });
      return false;
    }
  };

  const handleRowCancel = (row: any) => {

    // If we're canceling a new row, clear the adding state
    if (isAddingRow && row.id === newRow?.id) {
      setIsAddingRow(false);
      setNewRow(null);
    }

    setEditingRowId(null); // Clear editing state
  };

  // Context menu action handler
  const handleContextMenuAction = (action: ContextMenuAction, row: any) => {

    // Prevent editing rows with valid_flag = 0
    if (action === 'editSample') {
      // Check if the row has valid_flag = 0
      if (row.validFlag === 0) {
        setNotification({
          open: true,
          message: t('eno1.viewSamples.cannotEditInvalidRow', 'Only valid rows can be edited'),
          type: 'error',
        });
        return;
      }

      // Set this row for editing and open the edit modal
      if (isEditMode && hasEditPermission) {
        setEditingRowId(row.sampleDbId || row.id);
        // If sample status is Final, show comment as empty in edit mode
        const rowForEdit = row.sampleStatus === 'F' 
          ? { ...row, comment: false }
          : row;
        setSelectedRow(rowForEdit);
        setIsEditModalOpen(true);
      }
    } else if (action === 'editStratumData') {

      // Navigate to stratum data page
      if (fileId) {
        // For edit mode, navigate to edit mode of stratum page
        if (isEditMode) {
          navigate(`/eno1/messages/stratum/${fileId}?editMode=true`);
        }
        // For view mode, navigate to view mode of stratum page
        else {
          navigate(`/eno1/messages/stratum/${fileId}`);
        }
      }
    } else if (action === 'deleteAmendment' && row?.sampleStatus === 'D') {
      // Execute delete mutation using sampleId and current dataSetId (fileId)
      const sampleIdNum = Number(row?.sampleId);
      if (Number.isNaN(sampleIdNum)) {
        setNotification({
          open: true,
          message: t('eno1.viewSamples.deleteInvalidSample', 'Invalid sampleId for deletion'),
          type: 'error',
        });
        return;
      }
      deleteSampleMutation.mutate(
        { sampleId: sampleIdNum, fileId: numericFileId },
        {
          onSuccess: () => {
            setNotification({
              open: true,
              message: t('eno1.viewSamples.deleteSuccess', 'Amendment deleted successfully'),
              type: 'success',
            });
          },
          onError: (err: any) => {
            const message = err?.response?.data?.message || err?.message || t('eno1.viewSamples.deleteError', 'Failed to delete amendment');
            setNotification({ open: true, message, type: 'error' });
          },
        }
      );
    } else if (action === 'additionalSampleInfo') {
      // Implement additional sample information action logic here
      // This could open a modal with more detailed information
      setSelectedASIRow(row);
      setIsASIModalOpen(true);
    }
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRow(null);
    setEditingRowId(null);

    // If we were adding a new row but canceled, clear that state too
    if (isAddingRow) {
      setIsAddingRow(false);
      setNewRow(null);
    }
  };

  // Handle closing the ASI modal
  const handleCloseASIModal = () => {
    setIsASIModalOpen(false);
    setSelectedASIRow(null);
    // Refetch the table data to reflect any changes made in the ASI modal
    refetchDispatchData();
  };

  // Define custom context menu actions for the samples table
  const customContextMenuActions = useMemo(() => {
    // For edit mode, return actions based on row status
    if (isEditMode) {
      return [
        {
          action: 'editSample' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.editSample', 'Edit Sample'),
          icon: <EditIcon />,
          isVisible: (row: any) => true, // Only visible if valid_flag is not 0
        },
        {
          action: 'editStratumData' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.editStratumData', 'Edit Stratum Data'),
          icon: <AssessmentIcon />,
          isVisible: (row: any) => true, // Only visible if valid_flag is not 0
        },
        {
          action: 'deleteAmendment' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.deleteAmendment', 'Delete Amendment'),
          icon: <DeleteIcon />,
          isVisible: (row: any) => row?.sampleStatus === 'D', // Only visible when sample status is 'D'
        },
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.asi', 'ASI (Additional Sample Information)'),
          icon: <InfoOutlinedIcon />,
          isVisible: (row: any) => true, // Always visible
        },
      ];
    }
    // Options for view mode
    else if (isViewMode) {
      return [
        {
          action: 'editStratumData' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.viewStratumData', 'View Stratum Data'),
          icon: <AssessmentIcon />,
          isVisible: (row: any) => true, // Always visible in view mode
        },
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('eno1.viewSamples.contextMenu.asi', 'ASI (Additional Sample Information)'),
          icon: <InfoOutlinedIcon />,
          isVisible: (row: any) => true, // Always visible
        },
      ];
    }
    // Return empty array if neither edit nor view mode
    else {
      return [];
    }
  }, [isEditMode, isViewMode, t]);

  // Update the handlers for validation buttons to make API requests
  const handleValidateAndCorrect = async () => {
    if (fileId) {
      setValidateCorrectLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();
      
      try {
        // Use the React Query mutation hook instead of direct axios call
        const response = await validateFile.mutateAsync(numericFileId);

        // Handle successful response
        setNotification({
          open: true,
          message: t(
            'eno1.viewSamples.validateCorrectSuccess',
            'Validation and correction initiated successfully. File is validated.'
          ),
          type: 'success',
        });

        // Refresh the page after showing the validation notification
        // Add a small delay to ensure the notification is seen
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];
        
        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }
        
        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);
          
          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'eno1.viewSamples.validationErrors',
              'Validation failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          // Handle error with general message if no specific validation errors
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'eno1.viewSamples.validateCorrectError',
                  'Failed to initiate validation and correction'
                );

          console.error('Error validating and correcting:', error);
          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateCorrectLoading(false);
      }
    }
  };

  const handleValidateAndFinalize = async () => {
    if (fileId) {
      setValidateFinalizeLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();
      
      try {
        // Use the React Query mutation hook instead of direct axios call
        const response = await finalizeFile.mutateAsync(numericFileId);

        // Handle successful response
        setNotification({
          open: true,
          message: t(
            'eno1.viewSamples.validateFinalizeSuccess',
            'Validation and finalization initiated successfully. File is accepted.'
          ),
          type: 'success',
        });

        // Navigate to /eno1 after showing the acceptance notification
        // Add a small delay to ensure the notification is seen
        setTimeout(() => {
          navigate('/eno1');
        }, 1500);
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];
        
        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }
        
        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);
          
          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'eno1.viewSamples.finalizationErrors',
              'Finalization failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          // Handle error with general message if no specific validation errors
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'eno1.viewSamples.validateFinalizeError',
                  'Failed to initiate validation and finalization'
                );

          console.error('Error validating and finalizing:', error);
          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateFinalizeLoading(false);
      }
    }
  };

  // Helper function to check if a row is editable
  const isEditAllowed = (row: any): boolean => {
    return row?.validFlag !== 0;
  };

  const getTableContent = () => {
    if (!isValidFileId) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant='body1' color='error' data-testid="invalid-file-error">
            {t('eno1.viewSamples.invalidFileId', 'Invalid file ID provided')}
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box sx={{ p: 2, color: 'error.main' }} data-testid="error-container">
          <Typography variant='body1'>
            {t('eno1.viewSamples.error', 'Error loading sample data')}
          </Typography>
          {error instanceof Error && (
            <Typography variant='body2' sx={{ mt: 1 }}>
              {error.message}
            </Typography>
          )}
        </Box>
      );
    }

    // Process data to add ID for proper row editing and ensure consistent data format
    let dataWithIds = dispatchData.map((item: any) => {
      // Process the item to ensure consistent data types
      const processedItem = {
        ...item,
        id: item.sampleDbId || item.id, // Ensure each row has an id property for editing
        // Ensure sampleId is always a string
        sampleId: item.sampleId !== null && item.sampleId !== undefined ? String(item.sampleId) : '',
        // Add a className for disabled rows (valid_flag = 0)
        className: item.validFlag === 0 ? 'non-editable-row' : '',
      };

      // Preserve string representation for numeric fields if they come as strings
      // This ensures data consistency between what's displayed and what's sent to the API
      return processedItem;
    });

    // If we're adding a new row, add it to the data
    if (isAddingRow && newRow) {
      dataWithIds = [newRow, ...dataWithIds];
    }

    return (
      <Box>
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} data-testid="mobile-swipe-hint">
            <Tooltip title='Swipe left and right to view all data columns'>
              <InfoIcon color='primary' sx={{ mr: 1, fontSize: 18 }} />
            </Tooltip>
            <Typography variant='caption' color='text.secondary'>
              {t(
                'eno1.viewSamples.mobileNote',
                'Swipe horizontally to view all columns. Only showing essential columns on mobile.'
              )}
            </Typography>
          </Box>
        )}
        {isEditMode && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }} data-testid="edit-actions-container">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant='contained'
                color='secondary'
                startIcon={<VerifiedIcon />}
                onClick={handleValidateAndCorrect}
                disabled={validateCorrectLoading || !isValidateCorrectEnabled}
                fullWidth={isMobile}
                data-testid="validate-correct-button"
                title={
                  !isValidateCorrectEnabled
                    ? t(
                        'eno1.viewSamples.validateCorrectDisabled',
                        'Only drafts can be validated and corrected'
                      )
                    : ''
                }
              >
                {validateCorrectLoading ? (
                  <>
                    <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                    {t('eno1.viewSamples.validating', 'Validating...')}
                  </>
                ) : (
                  t('eno1.viewSamples.validateCorrect', 'Validate & Correct')
                )}
              </Button>
              {!isPlaygroundFile(fileDetails ?? { operatorByInboundPpo: null, operatorByOutboundPpo: null }) && (
                <Button
                  variant='contained'
                  color='success'
                  startIcon={!validateFinalizeLoading && <DoneAllIcon />}
                  onClick={handleValidateAndFinalize}
                  disabled={validateFinalizeLoading || !isValidateFinalizeEnabled}
                  fullWidth={isMobile}
                  data-testid="validate-finalize-button"
                  title={
                    !isValidateFinalizeEnabled
                      ? t('eno1.viewSamples.validateFinalizeDisabled', 'Already finalized')
                      : ''
                  }
                >
                  {validateFinalizeLoading ? (
                    <>
                      <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                      {t('eno1.viewSamples.finalizing', 'Finalizing...')}
                    </>
                  ) : (
                    t('eno1.viewSamples.validateFinalize', 'Validate & Finalize')
                  )}
                </Button>
              )}
              {isEditMode && (
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  disabled={isAddingRow || addSampleMutation.isPending}
                  fullWidth={isMobile}
                  data-testid="add-sample-button"
                >
                  {t('eno1.viewSamples.addSample', 'Add New Sample')}
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {/* Display validation errors if they exist */}
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 3 }} data-testid="validation-errors-container">
            <PaginatedErrorTable 
              errors={validationErrors} 
              fileName={`eNO1 File ID: ${fileId}`}
            />
          </Box>
        )}
        <Table
          data={dataWithIds}
          columns={filteredColumns}
          moduleName={t(
            'eno1.viewSamples.tableTitle',
            `eNO1 SAMPLES`
          )}
          totalCount={
            isAddingRow
              ? (dispatchResponse?.totalElements || 0) + 1
              : dispatchResponse?.totalElements
          }
          pageSize={pageSize}
          page={page}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          defaultSort={sorts.length > 0 ? sorts[0] : (sortField && sortDirection ? { column: sortField, direction: sortDirection } : undefined)}
          defaultSorts={sorts.length > 0 ? sorts : undefined}
          defaultFilters={filters.length > 0 ? filters : undefined}
          initialSearchColumn={searchColumn}
          initialSearchTerm={searchTerm}
          disableAutoSizing={false}
          viewMode={!isEditMode} // Set viewMode to false when in edit mode
          editMode={isEditMode}
          onCellEdit={isEditMode ? handleCellEdit : undefined}
          onRowSave={isEditMode ? handleRowSave : undefined}
          onRowCancel={isEditMode ? handleRowCancel : undefined}
          readOnlyColumns={['sampleDbId', 'fileId', 'version']} // Specify which columns should not be editable
          excludedColumns={[
            'asi',
            'sampleStatus',
            'stratum',
            'receivedWeightStratum',
            'sendingPpo',
            'receivingPpo',
            'sampledWeightStratum',
            'effectiveSamplingRateStratum',
            'received_weight_stratum',
            'sampled_weight_stratum',
            'effective_sampling_rate_stratum',
            'receivedReceptaclesStratum',
            'received_receptacles_stratum',
          ]}
          data-testid="samples-table"
          requiredFields={[
            'version', 'delFlag', 'validFlag',
            'mailCategory', 'formatCorrected', 'samplingMethod', 'netOrGrossAccounting',
            'samplingRate',  'oeFromCode',  'oeToCode',
            'samplingDate', 'receptacleNumber', 'despatchNumber', 'despatchDate',
            'grossKg', 'tareKg', 'netKg', 'pkg', 'gkg', 'ekg',
            'pitems', 'gitems', 'eitems', 'despatchMailSubclass', 'subsampleRate' 
          ]}
          textAreaColumns={['comment']}
          dateColumns={['samplingDate', 'despatchDate', 'sampling_date', 'despatch_date']}
          defaultReadOnlyColumns={isAddingRow ? ['validFlag', 'delFlag', 'comment'] : ['validFlag']}
          priorityReadOnlyColumns={['sampleId', 'version', 'changeBy']}
          rowEventHandlers={{
            onContextMenuAction: handleContextMenuAction,
          }}
          customActions={customContextMenuActions}
          hideDefaultActions={false} // Show default actions
          isRowEditable={isEditAllowed} // Pass the isEditAllowed function to determine if a row is editable
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='home-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title={`eNO1 Samples - File ID:  ${fileId} - ${fileDetails?.fileName}${isEditMode ? ' (Edit Mode)' : ''}`}
            subtitle='eNO1 Message'
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customStatus: true,
            }}
            customFilters={{
              status: statusFilter,
              onStatusChange: handleStatusFilterChange,
            }}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} height='100%'>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='eno1-samples-main-content'>
            {permissionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid='permission-error-alert'>
                You don't have permission to view this section
              </Alert>
            ) : isEditMode && !hasEditPermission ? (
              <Alert severity='warning' data-testid='edit-permission-error-alert'>
                You don't have permission to edit samples
              </Alert>
            ) : (
              <Box component={Paper} sx={{ mb: 2 }}>
                {fileDetails && isPlaygroundFile(fileDetails) && (
                  <Alert severity='warning' sx={{ mb: 1 }} data-testid='playground-warning-banner'>
                    <b>
                      {t(
                        'eno1.playgroundWarning',
                        'Please note that this is a playground file ({{fileName}})!',
                        { fileName: fileDetails.fileName }
                      )}
                    </b>
                  </Alert>
                )}
                <ContentPaper data-testid='content-paper'>{getTableContent()}</ContentPaper>
              </Box>
            )}
          </MainContent>
        </Grid>

        {/* Notification Snackbar */}
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.type}
          onClose={handleNotificationClose}
          data-testid='notification-snackbar'
        />

        {/* ENO1EditModal for sample editing */}
        {isEditMode && (
          <ENO1EditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSave={handleRowSave}
            row={selectedRow}
            columns={columns}
            excludedColumns={[
              'asi',
              'sampleStatus',
              'stratum',
              'receivedWeightStratum',
              'sendingPpo',
              'receivingPpo',
              'sampledWeightStratum',
              'effectiveSamplingRateStratum',
              'received_weight_stratum',
              'sampled_weight_stratum',
              'effective_sampling_rate_stratum',
              'receivedReceptaclesStratum',
              'received_receptacles_stratum',
            ]}
            requiredFields={[
              'mailCategory',
              'formatCorrected',
              'samplingMethod',
              'netOrGrossAccounting',
              'samplingRate',
              'oeFromCode',
              'oeToCode',
              'samplingDate',
              'receptacleNumber',
              'despatchNumber',
              'despatchDate',
              'grossKg',
              'tareKg',
              'netKg',
              'pkg',
              'gkg',
              'ekg',
              'pitems',
              'gitems',
              'eitems',
              'despatchMailSubclass',
              'subsampleRate',
            ]}
            textAreaColumns={['comment']}
            dateColumns={['samplingDate', 'despatchDate']}
            defaultReadOnlyColumns={
              isAddingRow ? ['validFlag', 'delFlag', 'comment'] : ['validFlag']
            }
            priorityReadOnlyColumns={['sampleId', 'version', 'changeBy']}
            readOnlyColumns={['sampleDbId', 'fileId', 'version']}
            title={
              isAddingRow
                ? t('eno1.viewSamples.addSample', 'Add New Sample')
                : t('eno1.viewSamples.editSample', 'Edit Sample')
            }
            data-testid='edit-sample-modal'
          />
        )}

        {/* ASI Modal for additional sample information */}
        <ASIModal
          isOpen={isASIModalOpen}
          onClose={handleCloseASIModal}
          row={selectedASIRow}
          dataSetId={numericFileId}
          fileDetails={fileDetails}
        />
      </Grid>
    </Box>
  );
};

export default ViewSamples;
